import React from "react";
import { Button, ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Standardized button variants for consistent usage across the app
export interface StandardButtonProps extends Omit<
  ButtonProps,
  "variant" | "size"
> {
  variant?:
    | "primary" // Main action buttons (bg-primary)
    | "brand" // Brand colored buttons (bg-brand) for auth pages
    | "secondary" // Secondary actions (outlined)
    | "outline" // Outlined buttons
    | "ghost" // Subtle buttons without background
    | "destructive" // Delete/dangerous actions
    | "success" // Success/confirmation actions
    | "link"; // Link-style buttons
  size?:
    | "sm" // Small buttons (tables, compact UI)
    | "default" // Standard size
    | "lg" // Large buttons (forms, CTAs)
    | "xl" // Extra large (hero sections)
    | "icon"; // Icon-only buttons
  shape?:
    | "rounded" // Default rounded corners
    | "pill" // Fully rounded (pill shape)
    | "square"; // Sharp corners
}

// Map our standardized variants to the underlying button system
const getVariantProps = (
  variant: StandardButtonProps["variant"],
): {
  variant: ButtonProps["variant"];
  className?: string;
  style?: React.CSSProperties;
} => {
  switch (variant) {
    case "primary":
      return {
        variant: "default",
        className:
          "bg-primary text-primary-foreground hover:opacity-90 transition-opacity",
      };
    case "brand":
      return {
        variant: "default",
        className: "bg-brand hover:bg-brand/90 text-white",
      };
    case "secondary":
      return {
        variant: "secondary",
        className:
          "bg-background hover:bg-primary text-black dark:text-white hover:text-white",
      };
    case "outline":
      return {
        variant: "outline",
        className:
          "border-primary text-foreground hover:!bg-primary hover:!text-primary-foreground transition-colors",
      };
    case "ghost":
      return {
        variant: "ghost",
        // Normal text respects theme; when the button is the active/selected
        // item (e.g. pagination `aria-current="page"` or `data-active`),
        // give it a primary background and white text so it remains readable.
        className:
          "text-slate-600 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-primary active:text-white [data-active]:bg-primary [data-active]:text-white aria-[current=page]:bg-primary aria-[current=page]:text-white",
      };
    case "destructive":
      return {
        variant: "destructive",
        className: "text-white hover:opacity-90 transition-opacity",
        style: { backgroundColor: "rgb(143, 26, 31)" },
      };
    case "success":
      return {
        variant: "default",
        className: "text-white hover:opacity-90 transition-opacity",
        style: { backgroundColor: "rgb(37, 82, 13)" },
      };
    case "link":
      return { variant: "link" };
    default:
      return { variant: "default" };
  }
};

const getSizeProps = (
  size: StandardButtonProps["size"],
  shape: StandardButtonProps["shape"],
): { size: ButtonProps["size"] } => {
  if (shape === "pill") {
    switch (size) {
      case "sm":
        return { size: "pill-sm" };
      case "lg":
        return { size: "pill-lg" };
      case "xl":
        return { size: "pill-xl" };
      case "icon":
        return { size: "icon" }; // Icons don't get pill shape
      default:
        return { size: "pill" };
    }
  }

  switch (size) {
    case "sm":
      return { size: "sm" };
    case "lg":
      return { size: "lg" };
    case "xl":
      return { size: "xl" };
    case "icon":
      return { size: "icon" };
    default:
      return { size: "default" };
  }
};

const getShapeClassName = (shape: StandardButtonProps["shape"]): string => {
  switch (shape) {
    case "square":
      return "rounded-none";
    case "pill":
      return ""; // Handled by size prop
    default:
      return "";
  }
};

export const StandardButton = React.forwardRef<
  HTMLButtonElement,
  StandardButtonProps
>(
  (
    {
      variant = "primary",
      size = "default",
      shape = "rounded",
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const variantProps = getVariantProps(variant);
    const sizeProps = getSizeProps(size, shape);
    const shapeClassName = getShapeClassName(shape);

    return (
      <Button
        ref={ref}
        variant={variantProps.variant}
        size={sizeProps.size}
        className={cn(variantProps.className, shapeClassName, className)}
        style={{ ...variantProps.style, ...style }}
        {...props}
      />
    );
  },
);

StandardButton.displayName = "StandardButton";

// Specific button components for common patterns
export const ButtonPrimary = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "variant">
>(({ shape = "rounded", ...props }, ref) => (
  <StandardButton ref={ref} variant="primary" shape={shape} {...props} />
));
ButtonPrimary.displayName = "ButtonPrimary";

export const ButtonBrand = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "variant">
>(({ shape = "pill", ...props }, ref) => (
  <StandardButton ref={ref} variant="brand" shape={shape} {...props} />
));
ButtonBrand.displayName = "ButtonBrand";

export const ButtonSecondary = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "variant">
>(({ shape = "rounded", ...props }, ref) => (
  <StandardButton ref={ref} variant="secondary" shape={shape} {...props} />
));
ButtonSecondary.displayName = "ButtonSecondary";

export const ButtonOutline = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "variant">
>(({ shape = "pill", ...props }, ref) => (
  <StandardButton ref={ref} variant="outline" shape={shape} {...props} />
));
ButtonOutline.displayName = "ButtonOutline";

export const ButtonGhost = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "variant">
>(({ ...props }, ref) => (
  <StandardButton ref={ref} variant="ghost" {...props} />
));
ButtonGhost.displayName = "ButtonGhost";

export const ButtonDestructive = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "variant">
>(({ shape = "pill", ...props }, ref) => (
  <StandardButton ref={ref} variant="destructive" shape={shape} {...props} />
));
ButtonDestructive.displayName = "ButtonDestructive";

export const ButtonSuccess = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "variant">
>(({ shape = "pill", ...props }, ref) => (
  <StandardButton ref={ref} variant="success" shape={shape} {...props} />
));
ButtonSuccess.displayName = "ButtonSuccess";

export const ButtonIcon = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "size">
>(({ variant = "ghost", ...props }, ref) => (
  <StandardButton ref={ref} variant={variant} size="icon" {...props} />
));
ButtonIcon.displayName = "ButtonIcon";

export const ButtonTable = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "size">
>(({ variant = "ghost", ...props }, ref) => (
  <StandardButton ref={ref} variant={variant} size="sm" {...props} />
));
ButtonTable.displayName = "ButtonTable";

export const ButtonForm = React.forwardRef<
  HTMLButtonElement,
  Omit<StandardButtonProps, "size" | "shape">
>(({ variant = "primary", ...props }, ref) => (
  <StandardButton
    ref={ref}
    variant={variant}
    size="lg"
    shape="pill"
    {...props}
  />
));
ButtonForm.displayName = "ButtonForm";
